import { signInWithGoogle, signInWithApple, SocialSignInError } from "./auth";
import {
  SocialProvider,
  buildOAuthStartUrl,
  stageOAuthParams,
} from "./oauthDialog";

function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

export type { SocialProvider };

export interface SocialAuthResult {
  success: boolean;
  error?: string;
}

interface OAuthMessage {
  success: boolean;
  provider?: SocialProvider;
  idToken?: string;
  accessToken?: string;
  nonce?: string;
  code?: string;
  error?: string;
}

function openOAuthDialog(url: string): Promise<OAuthMessage> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 40, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(result.error?.message || "Failed to open sign-in dialog"));
          return;
        }

        const dialog = result.value;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
          dialog.close();
          try {
            const message: OAuthMessage = JSON.parse((arg as { message: string }).message);
            resolve(message);
          } catch {
            reject(new Error("Invalid response from sign-in dialog"));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
          const eventArg = arg as { error?: number };
          if (eventArg.error === 12006) {
            resolve({ success: false, error: "Sign in was cancelled." });
          } else {
            reject(new Error("Sign-in dialog was closed unexpectedly."));
          }
        });
      }
    );
  });
}

function formatSocialError(err: unknown, provider: SocialProvider): string {
  const socialErr = err as SocialSignInError;

  if (socialErr.status === 404 || socialErr.status === 401) {
    const providerName = provider === "google" ? "Google" : "Apple";
    return `No CHLK account found for this ${providerName} identity. Sign in with your email and password instead.`;
  }

  if (socialErr.message) {
    return socialErr.message;
  }

  return "Something went wrong. Please try again.";
}

export async function startGoogleSignIn(): Promise<SocialAuthResult> {
  const nonce = generateNonce();
  stageOAuthParams("google", nonce);
  const startUrl = buildOAuthStartUrl("google", nonce);

  try {
    const message = await openOAuthDialog(startUrl);

    if (!message.success) {
      return { success: false, error: message.error || "Sign in was cancelled." };
    }

    if (!message.idToken) {
      return { success: false, error: "No authentication token received from Google." };
    }

    await signInWithGoogle(message.idToken, message.accessToken);
    return { success: true };
  } catch (err) {
    return { success: false, error: formatSocialError(err, "google") };
  }
}

export async function startAppleSignIn(): Promise<SocialAuthResult> {
  const nonce = generateNonce();
  stageOAuthParams("apple", nonce);
  const startUrl = buildOAuthStartUrl("apple", nonce);

  try {
    const message = await openOAuthDialog(startUrl);

    if (!message.success) {
      return { success: false, error: message.error || "Sign in was cancelled." };
    }

    if (!message.idToken) {
      return { success: false, error: "No authentication token received from Apple." };
    }

    await signInWithApple(message.idToken, message.nonce || nonce);
    return { success: true };
  } catch (err) {
    return { success: false, error: formatSocialError(err, "apple") };
  }
}
